import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { Document, BLOCKS, MARKS } from '@contentful/rich-text-types'
import classnames from 'classnames'
import React, {
  ForwardedRef,
  Fragment,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import { CONFIG_ENABLE_HOLIDAY_CUTOFF } from '@src/config/env'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { STATSIG_EXPERIMENTS, useStatsigExperimentBucket } from '@src/experiments/statsig-integration'
import { getSelectedColorFromSelectedOptions } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import { QUINCE_BOT_LOCATORS } from '@src/quince-site-common/src/constants/quinceBot'
import ContentfulProductMeasuringGuide from '@src/quince-site-common/src/domains/contentful/ContentfulMeasuringGuide'
import ContentfulPreOrderModal from '@src/quince-site-common/src/domains/contentful/ContentfulPreOrder'
import { ContentfulSizeChart } from '@src/quince-site-common/src/domains/contentful/ContentfulProduct'
import CustomSection from '@src/quince-site-common/src/domains/CustomSection'
import { MergedSizeChart } from '@src/quince-site-common/src/domains/MergedSizeChart'
import PriceDetailsForSSR from '@src/quince-site-common/src/domains/PriceDetailsForSSR'
import Product from '@src/quince-site-common/src/domains/Product'
import ProductOptionValue, {
  ProductOption,
  ProductOptionDisplayType,
  ProductOptionName,
} from '@src/quince-site-common/src/domains/ProductOption'
import { InventoryInfoSourceType } from '@src/quince-site-common/src/domains/ProductPurchaseInfo'
import { BaseProductVariant, ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import RichText from '@src/quince-site-common/src/domains/RichText'
import { ISwatch } from '@src/quince-site-common/src/domains/Swatch'
import productHelper, {
  checkProductUpholsteredBed,
  getIsWhiteGloveDelivery,
  isProductASofa,
  isProductCaviar,
  isProductVitamin,
  THIRD_PARTY,
} from '@src/quince-site-common/src/helpers/productHelper'
import useKlarnaModule from '@src/quince-site-common/src/hooks/useKlarnaModule'
import useTrueFitWidgetVisibility from '@src/quince-site-common/src/organisms/true-fit/hooks/useTrueFitWidgetVisibility'
import { TrueFitWidget } from '@src/quince-site-common/src/organisms/true-fit/true-fit-widgets'
import { EDDDetails } from '@src/quince-site-common/src/services/eddService/eddService.types'
import productService from '@src/quince-site-common/src/services/productService'
import { GROUP_OPTION_NAMES, CAVIAR_PREORDER_MESSAGE } from '@src/quince-site-common/src/utils/constants'
import {
  getIsFinalSaleProduct,
  getOverriddenOfferMessage,
} from '@src/quince-site-common/src/utils/festivalOffersUtility'
import { getSanitizedHtml } from '@src/quince-site-common/src/utils/getSanitizedHtml'
import removeTargetAttributeForHashAnchorTags from '@src/quince-site-common/src/utils/removeTargetAttributeForHashAnchorTags'
import OptionSelector from '@src/quince-site-pdp/src/atoms/optionSelector/OptionSelector'
import useProductPageContext from '@src/quince-site-pdp/src/hooks/useProductContext'
import ShowSimilarLabel from '@src/quince-site-pdp/src/organisms/productPageSections/showSimilar/ShowSimilarLabel'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { find } from '@src/utils/functions/utils'

import { TrackConfig } from '../../hocs/withTracking'
import useHash from '../../hooks/useHash'
import { RichTextProps } from '../../molecules'
import { getIsForcedTextOption } from '../../molecules/optionSelector/productVariantSelector/hooks/useProductVariantSelector.helper'
import AirlineCompatibility from '../airlineCompability/AirlineCompability'
import Button, { BUTTON_TYPES } from '../button'
import Icon from '../icon'
import Modal from '../modal'
import ProductArrivalMessage from '../productArrivalMessage'
import ProductCTA from '../productCTA'
import { CTA_TYPE } from '../productCTA/productCTAConstants'
import ProductMessage from '../productCTA/productMessage'
import useProductCTAType from '../productCTA/useProductCTAType'
import ProductInfo from '../productInfo'
import ProductOfferMessage from '../productOfferMessage'
import SiblingProductOptions, { SiblingProductOptionType } from '../siblingProductOptions'
import { SizeGuide } from '../sizeGuide'

import { RETURN_POLICY_SUBTEXT } from './constants/constant'
import Delivery from './icons/delivery.inline.svg'
import Installments from './icons/installments.inline.svg'
import Lock from './icons/lock.inline.svg'
import Return from './icons/return.inline.svg'
import RightArrow from './icons/rightArrow.inline.svg'
import { RightMiniPDPAddToCart } from './MiniPDPRightContainer'
import styles from './productForm.module.scss'

const AIRLINE_COMPATIBILITY_HASH = 'airline-compatibility'
const SHOW_MORE_CHARACTER_COUNT = 200

function getProductDescription(description: string, showReadMore: boolean | undefined) {
  if (showReadMore) {
    // Remove all the HTML elements
    const _description = description.replace(/<[^>]*>/g, '')

    if (_description.length > SHOW_MORE_CHARACTER_COUNT) {
      return `<p>${_description.slice(0, SHOW_MORE_CHARACTER_COUNT).trim()}<span data-readmore='true' class='${styles.readMore}'>Read More</span></p>`
    }
  }

  return (
    getSanitizedHtml(description) +
    "<span data-readmore='false' class=" +
    (styles.readLess || '') +
    '> Read Less</span>'
  )
}

function shouldShowOnlySizeChart(
  productOptions: ProductOption[] | undefined,
  sizeChart: ContentfulSizeChart | undefined,
  mergedSizeChart: MergedSizeChart[] | undefined,
  hideSizeChart: boolean | undefined,
) {
  const sizeOption = find(productOptions, ({ name }) => name === ProductOptionName.Size)
  const showOnlySizeChart = !hideSizeChart && sizeOption?.isHidden && !!(sizeChart || mergedSizeChart)

  return showOnlySizeChart
}

export interface PreOrderInfo {
  date: string
  onPreOrder: () => void
}

export interface MadeToOrderInfo {
  madeToOrderText: RichText
}

export interface VariantPurchaseInfo {
  availableInventory: number
  showWaitList: boolean
  showMadeToOrder: boolean
  hasJoinedWaitList: boolean
  backInStockDate: string | undefined
  madeToOrderText: Document | undefined
  isPreorder: boolean
  preOrderShippingDate?: string
  selectedVariantId?: number
  isNewPreorderProduct?: boolean
  inventoryInfoSource: InventoryInfoSourceType
  selectedInternalVariantId?: number[]
  isAnySelectedVariantInStock?: boolean
  eddDetail?: EDDDetails
}

export interface ProductFormProps {
  className?: string
  title: string
  averageRating?: number
  reviewCount?: number
  productOptions: ProductOption[]
  selectedOptions: ProductVariantOption[]
  isWishListed?: boolean
  formattedSalePrice: string
  festivalOfferPrice?: number
  formattedFestivalOfferPrice?: string
  formattedTraditionalPrice?: string
  productInfo?: RichTextProps
  productInfoHeaderClassName?: string
  onOptionSelect: (optionName: string, selectedValue: string) => void
  onAddToCart: (isPreorder?: boolean) => void
  onWishlistToggle: () => void
  onReviewSummaryClick: () => void
  description?: string
  preOrder?: PreOrderInfo
  hideProductDescription?: boolean
  hideReviewSummary?: boolean
  hideSizeChart?: boolean
  hideOfferMessage?: boolean
  hideMtoText?: boolean
  hideWishListIcon?: boolean
  hidePayLater?: boolean
  hideFooter?: boolean
  selectedVariantPurchaseInfo?: VariantPurchaseInfo
  onJoinWaitList: (email?: string | undefined) => void
  sizeChart?: ContentfulSizeChart | undefined
  mergedSizeChart?: MergedSizeChart[] | undefined
  measuringGuides?: ContentfulProductMeasuringGuide[]
  isAddToCartInProgress?: boolean
  isSdcProduct?: boolean
  product?: Product
  errorMessage?: string
  optionsContainerClassName?: string
  isReturnable?: boolean
  subDescriptionText?: string
  priceDetails?: PriceDetailsForSSR
  savingPercentage?: number
  offerMessageFromPC?: string
  salePrice: number
  brandName?: string
  brandLink?: string
  traditionalDiscountMessage?: string
  selectedSiblingProductId?: number
  onSiblingOptionSelect?: (val: string) => void
  preOrderModalData?: ContentfulPreOrderModal
  disableAddToCart?: boolean
  airlineCompatibilityListings?: CustomSection[] | undefined
  areAllVariantOOS?: boolean
  addToCartRef?: any
  onSwatchLinkClick?: (swatchData: ISwatch) => void
  shouldDisableAirlineCompatibility?: boolean
  onBuyNowCart?: () => void
}

const FreeShippingText = ({ isWhiteGloveDelivery }: { isWhiteGloveDelivery: boolean }) => {
  return (
    <div
      className={classnames(styles.container__info__main__text, {
        [styles.container__whiteGloveTextWidth ?? '']: isWhiteGloveDelivery,
      })}
    >
      {isWhiteGloveDelivery ? (
        <>
          Free white
          <br /> glove delivery
        </>
      ) : (
        'Free shipping'
      )}
    </div>
  )
}

const ViewFullProductDetails = ({ text }: { text?: string }) => {
  const { setShowFullPDP } = useProductPageContext()

  function handleClick() {
    setShowFullPDP(true)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className={styles.viewFullProdWrapper}>
      <button className={styles.btn} onClick={handleClick}>
        {text || 'VIEW FULL PRODUCT DETAILS'}
      </button>
    </div>
  )
}

export const KlarnaWrapper = ({
  hidePayLater,
  salePrice,
  festivalOfferPrice,
  wrapperClassname,
}: {
  hidePayLater?: boolean
  salePrice?: number
  festivalOfferPrice?: number
  wrapperClassname?: string
}) => {
  return !hidePayLater && salePrice ? (
    <div className={classnames(wrapperClassname, styles.container__szmessage)}>
      <klarna-placement
        data-key="credit-promotion-auto-size"
        data-locale="en-US"
        data-purchase-amount={Math.floor((festivalOfferPrice || salePrice) * 100)}
      />
    </div>
  ) : (
    <></>
  )
}

export const ProductDescFooter = ({
  hideFooter,
  isReturnable,
  product,
  selectedOptions,
}: {
  hideFooter: boolean | undefined
  isReturnable: boolean | undefined
  product: Product | undefined
  selectedOptions?: ProductVariantOption[]
}) => {
  const [showEasyReturnModal, setShowEasyReturnModal] = useState(false)
  const secondChargingExpBucket = useStatsigExperimentBucket(STATSIG_EXPERIMENTS.SECOND_RETURNS_CHARGING)
  const isInChargingExpBucket = secondChargingExpBucket === 'Show'

  if (hideFooter) return <></>
  const isWhiteGloveDelivery = getIsWhiteGloveDelivery(product)

  const easyReturnsTrackConfig: TrackConfig = {
    trackGA: true,
    trackInternal: true,
    category: 'checkout',
    action: 'easy_returns',
    context: {
      product_id: product?.shopifyProductId,
      title: product?.title,
      tags: product?.tags,
      subClassId: product?.subClassId,
      color: selectedOptions?.find(option => option.name === 'Color')?.value,
      size: selectedOptions?.find(option => option.name === 'Size')?.value,
    },
  }

  const handleEasyReturnClick = () => {
    setShowEasyReturnModal(prev => !prev)
    trackEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, easyReturnsTrackConfig)
  }

  return (
    <div className={styles.container__info}>
      <div className={styles.container__info__main}>
        <div className={styles.container__info__main__icon}>
          <Icon iconSvg={Delivery} className={styles.container__info__main__icon__delivery} />
        </div>
        <FreeShippingText isWhiteGloveDelivery={isWhiteGloveDelivery} />
      </div>
      {isReturnable && !product?.hasRestockingFee ? (
        <div className={styles.container__info__main}>
          <div className={styles.container__info__main__icon}>
            <Icon iconSvg={Return} className={styles.container__info__main__icon__return} />
          </div>
          {isInChargingExpBucket ? (
            <div
              className={classnames(styles.container__info__main__text, styles.easyReturns)}
              onClick={handleEasyReturnClick}
            >
              <p>Easy returns</p>
              <span className={styles.iconWrapper}>
                <Icon className={styles.icon} iconSvg={RightArrow} />
              </span>
            </div>
          ) : (
            <div className={styles.container__info__main__text}>Easy returns</div>
          )}
        </div>
      ) : (
        <div className={styles.container__info__main}>
          <div className={styles.container__info__main__icon}>
            <Installments className={styles.container__info__main__icon__installments} />
          </div>
          <div
            className={classnames(styles.container__info__main__text, {
              [styles.infoTextNewLine ?? '']: isWhiteGloveDelivery,
            })}
          >
            <span>Pay in</span> installments
          </div>
        </div>
      )}
      <div className={styles.container__info__main}>
        <div className={styles.container__info__main__icon}>
          <Icon iconSvg={Lock} className={styles.container__info__main__icon__lock} />
        </div>
        <div
          className={classnames(styles.container__info__main__text, {
            [styles.infoTextNewLine ?? '']: isWhiteGloveDelivery,
          })}
        >
          <span>Secure </span>payment
        </div>
      </div>
      {showEasyReturnModal && isInChargingExpBucket && (
        <Modal
          open={showEasyReturnModal}
          className={styles.easyReturnModal}
          modalWrapperClassName={styles.easyReturnModalWrapper}
          showCloseButton={true}
          onClose={handleEasyReturnClick}
        >
          <div>
            <h3 className={styles.modalHeader}>Return Policy</h3>
            <div className={styles.subText}>{RETURN_POLICY_SUBTEXT}</div>
            <a className={styles.redirectLink} href={`${window.location.origin}/shipping-returns`}>
              Learn More
            </a>
          </div>
        </Modal>
      )}
    </div>
  )
}

const ProductForm = React.forwardRef(function ProductFormEl(
  props: ProductFormProps,
  ref: ForwardedRef<HTMLDivElement | null>,
) {
  const productFormRef = useRef<HTMLDivElement>(null)

  const newColorGroupings = Array.from(
    new Set(
      props?.product?.variants
        ?.filter(variant => variant.colorGrouping != null && variant.colorGrouping != 'Core Color')
        .map(variant => variant.colorGrouping),
    ),
  ) as string[]

  const selectedVariant = useMemo(
    () =>
      props.product
        ? productHelper.getVariantsForOptions(props.product, props.selectedOptions)[0]
        : ({} as BaseProductVariant),
    [props.product, props.selectedOptions],
  )

  useImperativeHandle(ref, () => {
    if (!productFormRef.current) {
      throw new Error('productFormRef is not assigned')
    }
    return productFormRef.current
  })

  const {
    showFullPDP,
    setShowFullPDP,
    productPageExpData: {
      productFormWrapperClass,
      moveAddToCartBtnToRight,
      showViewFullProductDetails,
      hideProductDetail,
      hideAddToWishlist,
      hideReviewCount,
      viewFullProductText,
      hideKlarna,
      productInfoHeaderTextClassName,
      hideProductDescFooter,
      hideReviewLabel,
      productFormOptionsClass,
      productInfoReviewClassName,
      inStockMsgClass,
      showReadMoreInDesc,
      showInlineSizeChart,
      hideSizeChart,
    },
  } = useProductPageContext()
  const [showReadMore, setShowReadMore] = useState(showReadMoreInDesc)

  const isFabricMergedProduct = props.product?.addOnProductOptionGroups?.find(
    option => option.groupOptionName == GROUP_OPTION_NAMES.FABRIC,
  )
  const initialSwatch = isFabricMergedProduct ? [] : [props?.product?.swatch]

  const classes = classnames(styles.container, props.className, productFormWrapperClass)
  const [showSizeChart, setShowSizeChart] = useState(false)
  const [showAirlineCompatibility, setShowAirlineCompatibility] = useState(false)
  const [swatches, setSwatches] = useState(initialSwatch)

  const optionNameSelectedValueMap = props.selectedOptions.reduce((map: Record<string, string>, option) => {
    map[option.name] = option.value
    return map
  }, {})

  const fetchProductWithSwatch = async () => {
    const products = await productService.getProducts({
      shopifyProductIds: props?.product?.addOnProductOptionGroups
        ?.find(option => option.groupOptionName == GROUP_OPTION_NAMES.FABRIC)
        ?.groupItems?.map(item => item?.shopifyProductId),
    })
    let currentFabricSelectedSwatch
    const productSwatches = products
      ?.filter(product => {
        if (product.shopifyProductId == props?.product?.shopifyProductId) {
          currentFabricSelectedSwatch = product?.swatch
          return false
        } else {
          return true
        }
      })
      .map(product => product?.swatch)
    setSwatches([currentFabricSelectedSwatch, ...productSwatches])
  }
  useEffect(() => {
    if (props?.product?.addOnProductOptionGroups?.find(option => option.groupOptionName == GROUP_OPTION_NAMES.FABRIC))
      fetchProductWithSwatch()
  }, [props.product])

  const [hash] = useHash()

  useEffect(() => {
    if (hash === AIRLINE_COMPATIBILITY_HASH) {
      displayAirlineCompatbility()
    }
  }, [hash])

  const handleSizeChart = () => {
    setShowSizeChart(true)
  }

  const displayAirlineCompatbility = () => {
    setShowAirlineCompatibility(true)
  }

  const hideAirlineCompatbility = () => {
    setShowAirlineCompatibility(false)
  }

  const productFormContentRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    removeTargetAttributeForHashAnchorTags(productFormContentRef.current)
  }, [])

  const selectedVariantAvailableInventory = props.selectedVariantPurchaseInfo?.availableInventory || 0

  const renderSwatchSelectionButton = useCallback(() => {
    return swatches?.filter(item => item)?.length ? (
      <button
        className={styles.freeSwatchText}
        onClick={() => {
          props.onSwatchLinkClick?.(swatches)
        }}
      >
        Order Free Swatches
      </button>
    ) : null
  }, [swatches, props.onSwatchLinkClick])

  useKlarnaModule(props.salePrice)

  const { showTruefitWidget } = useTrueFitWidgetVisibility(props.product)

  const addOnOptions = props.product?.addOnProductOptionGroups?.map(group => {
    return {
      node: (
        <SiblingProductOptions
          siblingProductOptionGroup={group}
          displayType={props.onSiblingOptionSelect ? SiblingProductOptionType.BUTTON : SiblingProductOptionType.LINK}
          selectedProductId={props.selectedSiblingProductId}
          selectedProductOptions={props.selectedOptions}
          onSelect={props.onSiblingOptionSelect}
          className={styles.container__options}
          labelClassName={styles.container__label}
          optionSelectorClassName={styles.container__options__option}
          optionClassname={styles.container__options__optionWrapper}
        />
      ),
      displaySequence: group.optionDisplaySequence,
      key: group.groupId,
    }
  })

  const _productOptions = props.productOptions
  let updatedProductOptions = _productOptions
  if (newColorGroupings?.length && props.product) {
    updatedProductOptions = productHelper.updateProductOptionsWithNewColorOptions(
      props.product,
      props.productOptions,
      newColorGroupings,
    ) as ProductOption[]
    updatedProductOptions.sort((a, b) => a.displaySequence - b.displaySequence)
  }

  const productOptions = updatedProductOptions
    .filter(po => !po.isHidden)
    .map((productOption, index, arr) => {
      const shouldReduceMarginBottom =
        (newColorGroupings?.length && index + 1 <= arr.length - 1 && arr[index + 1]?.name == ProductOptionName.Color) ||
        false
      return {
        node: renderProductOption(
          productOption,
          optionNameSelectedValueMap,
          props.onOptionSelect,
          props.hideSizeChart || hideSizeChart,
          props.sizeChart,
          handleSizeChart,
          props.product,
          props.selectedOptions,
          props.optionsContainerClassName,
          props.mergedSizeChart,
          undefined,
          undefined,
          undefined,
          productFormOptionsClass,
          showInlineSizeChart,
          showTruefitWidget,
          renderSwatchSelectionButton,
          shouldReduceMarginBottom,
        ),
        displaySequence: productOption.displaySequence,
        key: productOption.name,
      }
    })

  const combinedProductOptions = [...(addOnOptions || []), ...productOptions]
  combinedProductOptions.sort((a, b) => a.displaySequence - b.displaySequence)

  const productCTAType = useProductCTAType({
    selectedVariantPurchaseInfo: props.selectedVariantPurchaseInfo,
  })

  const offerMessage = getOverriddenOfferMessage(props.offerMessageFromPC, props.product, props.selectedOptions)

  const isProductCaviarFlag = isProductCaviar(props.product)

  const isFinalSaleProduct = getIsFinalSaleProduct(props.product, props.selectedOptions)

  const selectedVariantIdsByProductId = useMemo(() => {
    if (!props.product?.id) return {}

    return {
      [props.product.id]: props.selectedVariantPurchaseInfo?.selectedInternalVariantId || [],
    }
  }, [props.product?.id, props.selectedVariantPurchaseInfo?.selectedInternalVariantId])

  const variantIdsByProductId = useMemo(
    () => (props.product?.id ? { [props.product.id]: props.product.variants.map(variant => variant.id) } : {}),
    [props.product?.id, props.product?.variants],
  )

  const showVariantAvailableInventory =
    ![CTA_TYPE.WAITLIST, CTA_TYPE.PREORDER].includes(productCTAType) &&
    selectedVariantAvailableInventory > 0 &&
    selectedVariantAvailableInventory < 5

  const shouldShowDeliveryDetails = props.product ? productHelper.shouldShowDeliveryInformation(props.product) : false

  const showOnlySizeChart = shouldShowOnlySizeChart(
    props.productOptions,
    props.sizeChart,
    props.mergedSizeChart,
    props.hideSizeChart || hideSizeChart,
  )
  const isProductSofa = isProductASofa(props.product)
  const isProductUpholsteredBed = checkProductUpholsteredBed(props.product)

  const shouldShowMTOText =
    !props.hideMtoText &&
    props.selectedVariantPurchaseInfo?.showMadeToOrder &&
    props.selectedVariantPurchaseInfo.madeToOrderText &&
    !isProductSofa &&
    !isProductUpholsteredBed

  const shippingInfoText = props.product && productHelper.getShippingInfoText(props.product)

  const isVitamin = isProductVitamin(props.product)

  return (
    <>
      <div ref={productFormRef} className={classes}>
        <ProductInfo
          productInfoHeaderClassName={props.productInfoHeaderClassName}
          hideReviewSummary={props.hideReviewSummary}
          productTitle={props.title}
          formattedSalePrice={props.formattedSalePrice}
          formattedTraditionalPrice={props.formattedTraditionalPrice}
          isWishListed={props.isWishListed}
          avgRating={props.averageRating}
          reviewCount={props.reviewCount}
          onWishListToggle={props.onWishlistToggle}
          hideWishListIcon={props.hideWishListIcon || hideAddToWishlist}
          onReviewSummaryClick={props.onReviewSummaryClick}
          savingPercentage={props.savingPercentage}
          brandName={props.brandName}
          brandLink={props.brandLink}
          traditionalDiscountMessage={props.traditionalDiscountMessage}
          formattedFestivalOfferPrice={props.formattedFestivalOfferPrice}
          showFullPDP={showFullPDP}
          hideReviewCount={hideReviewCount}
          hideReviewLabel={hideReviewLabel}
          productInfoHeaderTextClassName={productInfoHeaderTextClassName}
          productInfoReviewClassName={productInfoReviewClassName}
          showDiscount={selectedVariant?.vendorType !== THIRD_PARTY}
          pricePerServing={selectedVariant?.pricePerServing}
          traditionalPricePerServing={selectedVariant?.traditionalPricePerServing}
          isVitamin={isVitamin}
        />
        {combinedProductOptions.map(option => (
          <Fragment key={option.key}>{option.node}</Fragment>
        ))}
        {showOnlySizeChart && renderSizeChartButton(handleSizeChart)}
        {(shouldShowMTOText || !props.isReturnable) && (
          <div className={styles.container__mtoText}>
            {shouldShowMTOText ? (
              <>
                Made To Order:{' '}
                {documentToReactComponents(
                  props.selectedVariantPurchaseInfo?.madeToOrderText,
                  documentToReactComponentsTextOptions,
                )}
              </>
            ) : (
              shippingInfoText && (
                <div dangerouslySetInnerHTML={{ __html: getSanitizedHtml(shippingInfoText as string) }} />
              )
            )}
            {!props.isReturnable && (
              <div className={classnames(styles.container__mtoText__subText, 'd-block')}>
                This product is not returnable
              </div>
            )}
          </div>
        )}
        {/* <EarlyShipmentTimeLabel
            productId={props.product?.shopifyProductId}
            varient={EarlyShipmentTimeLabel.VARIENT.PRODUCT_FORM}
          /> */}
        {props.subDescriptionText && (
          <div className={styles.container__subDescText}>
            {
              <div
                className={classnames(styles.container__subDescText__subText, {
                  [styles.container__subDescText__textColorBlack]: props.isReturnable,
                })}
                dangerouslySetInnerHTML={{ __html: getSanitizedHtml(props.subDescriptionText) }}
              />
            }
          </div>
        )}
        <ProductOfferMessage
          hideOfferMessage={props.hideOfferMessage}
          offerMessageFromPC={offerMessage}
          productId={props.product?.shopifyProductId}
          showFinalSaleMessage={isFinalSaleProduct}
        />
        {CONFIG_ENABLE_HOLIDAY_CUTOFF &&
        !productHelper.isGiftCard(props.product?.shopifyProductId) &&
        !props.selectedVariantPurchaseInfo?.isPreorder &&
        shouldShowDeliveryDetails &&
        props.selectedVariantPurchaseInfo?.isAnySelectedVariantInStock ? (
          <ProductArrivalMessage
            allProductIdsVsVariantIdsMap={variantIdsByProductId}
            selectedProductIdsVsVariantIdsMap={selectedVariantIdsByProductId}
          />
        ) : null}
        <ProductMessage
          type={productCTAType}
          selectedVariantPurchaseInfo={props.selectedVariantPurchaseInfo}
          preOrderModalData={props.preOrderModalData}
          customMessage={isProductCaviarFlag ? CAVIAR_PREORDER_MESSAGE : undefined}
        />
        {showVariantAvailableInventory && (
          <div className={styles.selected_variant_available_inventory_message}>
            Hurry! Only {selectedVariantAvailableInventory} left
          </div>
        )}
        {!moveAddToCartBtnToRight && (
          <>
            {props.errorMessage && (
              <div className={classnames(styles.container__error_message)}>{props.errorMessage}</div>
            )}
            <ProductCTA
              type={productCTAType}
              onAddToCart={props.onAddToCart}
              isAddToCartInProgress={props.isAddToCartInProgress}
              selectedVariantPurchaseInfo={props.selectedVariantPurchaseInfo}
              onJoinWaitList={props.onJoinWaitList}
              showVariantAvailableInventory={showVariantAvailableInventory}
              product={props.product}
              areAllVariantOOS={props.areAllVariantOOS}
              addToCartRef={props.addToCartRef}
              onBuyNow={props.onBuyNowCart}
            />
          </>
        )}
        {showViewFullProductDetails ? <ViewFullProductDetails text={viewFullProductText} /> : <></>}
        {moveAddToCartBtnToRight ? null : (
          <>
            {!hideKlarna && (
              <KlarnaWrapper
                hidePayLater={props.hidePayLater}
                salePrice={props.salePrice}
                festivalOfferPrice={props.festivalOfferPrice}
              />
            )}
            <ShowSimilarLabel showIfAvailable={!!(props.preOrder || props.selectedVariantPurchaseInfo?.showWaitList)} />
            {!hideProductDescFooter && (
              <ProductDescFooter
                hideFooter={props.hideFooter}
                isReturnable={props.isReturnable}
                product={props.product}
                selectedOptions={props?.selectedOptions}
              />
            )}
          </>
        )}
        {hideProductDetail
          ? null
          : !props.hideProductDescription &&
            props.description && (
              <div
                className={classnames(styles.container__wrapper, {
                  [styles.showReadMore ?? '']: showReadMore,
                  [styles.showReadMoreInDesc ?? '']: showReadMoreInDesc,
                })}
              >
                <div
                  className={styles.container__content}
                  ref={productFormContentRef}
                  onClick={e => {
                    if ('readmore' in (e.target as HTMLElement).dataset) {
                      if ((e.target as HTMLElement).dataset.readmore === 'true') {
                        setShowReadMore(false)
                      } else {
                        setShowReadMore(true)
                      }
                    }
                  }}
                  dangerouslySetInnerHTML={{
                    __html: getProductDescription(props.description, showReadMore),
                  }}
                ></div>
              </div>
            )}
      </div>
      <RightMiniPDPAddToCart
        shouldShow={moveAddToCartBtnToRight}
        hidePayLater={props.hidePayLater}
        salePrice={props.salePrice}
        festivalOfferPrice={props.festivalOfferPrice}
        type={productCTAType}
        onAddToCart={props.onAddToCart}
        isAddToCartInProgress={props.isAddToCartInProgress}
        selectedVariantPurchaseInfo={props.selectedVariantPurchaseInfo}
        onJoinWaitList={props.onJoinWaitList}
        showVariantAvailableInventory={showVariantAvailableInventory}
        product={props.product}
        hideFooter={props.hideFooter}
        isReturnable={props.isReturnable}
        setShowFullPDP={setShowFullPDP}
        hideKlarna={hideKlarna}
        errorMessage={props.errorMessage}
        inStockMsgClass={inStockMsgClass}
      />
      {props.sizeChart && (
        <SizeGuide
          showSizeChartModal={showSizeChart}
          sizeChart={props.sizeChart}
          mergedSizeChart={props.mergedSizeChart || []}
          onSizeChartModalClose={() => {
            setShowSizeChart(false)
          }}
          measuringGuides={props.measuringGuides || []}
        />
      )}
      {!props.shouldDisableAirlineCompatibility && showAirlineCompatibility && (
        <AirlineCompatibility
          show={showAirlineCompatibility}
          onCloseAirlineCompatbility={hideAirlineCompatbility}
          airlineCompatibilityListingCS={props.airlineCompatibilityListings}
        />
      )}
    </>
  )
})

const renderSizeChartButton = (
  handleSizeChart: (e: React.MouseEvent<HTMLButtonElement>) => void,
  showInlineSizeChart?: boolean,
) => (
  <Button
    type={BUTTON_TYPES.SECONDARY_LINK}
    onClick={handleSizeChart}
    className={classnames(styles.container__label__sizeChart, {
      [styles.sizeChart_show ?? '']: showInlineSizeChart,
    })}
    id={TEST_LOCATORS.SIZE_CHART_BUTTON}
  >
    Size Chart
  </Button>
)

export const documentToReactComponentsTextOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => {
      return children.map(child => {
        if (typeof child === 'string') {
          return <span dangerouslySetInnerHTML={{ __html: getSanitizedHtml(child) }} />
        }
        return child
      })
    },
  },
  renderMark: {
    [MARKS.BOLD]: text => {
      return <span className="font-weight--500" dangerouslySetInnerHTML={{ __html: getSanitizedHtml(text) }} />
    },
  },
}

export const renderProductOption = (
  productOption: ProductOption,
  optionNameSelectedValueMap: Record<string, string>,
  onOptionSelect: (name: string, value: string) => void,
  hideSizeChart?: boolean,
  sizeChart: ContentfulSizeChart | undefined,
  handleSizeChart: () => void,
  product?: Product,
  selectedOptions: ProductVariantOption[],
  optionsContainerClassName?: string,
  mergedSizeChart?: MergedSizeChart[],
  legendClassName?: string,
  optionSelectorClassName?: string,
  optionDisplayTypesVsClassName?: Record<string, string>,
  productFormOptionsClass?: string,
  showInlineSizeChart?: boolean,
  showTruefitWidget?: boolean,
  renderSwatchSelectionButton: () => React.ReactNode,
  shouldReduceMarginBottom?: boolean,
) => {
  if (getIsForcedTextOption(product?.id)) {
    productOption = new ProductOption({
      ...productOption,
      values: productOption.values?.map(v => {
        return { ...v, displayType: ProductOptionDisplayType.TEXT }
      }),
    })
  }
  const poValueDisplayNameMap = productOption.values?.reduce((map: Record<string, string>, option) => {
    map[option.value] = option.displayName || option.value
    return map
  }, {})
  // TODO(STRFRNT-2867) - remove this after release
  let optionValues: ProductOptionValue[] = []
  const isCustomizationInputOption =
    productOption.name === ProductOptionName.Customization && productOption.displayType === 'Input'
  const isEngravingInputOption =
    productOption.name === ProductOptionName.Engraving && productOption.displayType === 'Text Box'

  if (isCustomizationInputOption) {
    optionValues = [
      new ProductOptionValue({
        displayType: ProductOptionDisplayType.DEPRECATED_INPUT,
        value: '',
      }),
    ]
  } else if (isEngravingInputOption) {
    optionValues = [
      new ProductOptionValue({
        displayType: ProductOptionDisplayType.INPUT,
        value: '',
        maxCharacterLimit: productOption.maxCharacterLimit,
      }),
    ]
  } else {
    optionValues = productOption.values || []
  }

  if (productOption.isHidden) return null

  // Hardcoding required as productOption.Name comes from API and can be used for tracking
  // Firmness productOptionName is color
  const isAllRoundOptions = (() => {
    const excludedDisplayNames = ['Firmness', 'Value']
    return productOption.name === ProductOptionName.Color && !excludedDisplayNames.includes(productOption.displayName)
  })()

  return (
    <fieldset
      className={classnames(styles.container__options, optionsContainerClassName, productFormOptionsClass, {
        [styles.container__options__colorOptionGroup ?? '']: shouldReduceMarginBottom,
      })}
      key={productOption.name}
    >
      <legend
        data-testid={`legend_${productOption.name}`}
        className={classnames(styles.container__label, legendClassName)}
      >
        {productOption.displayName || productOption.name}
        {/* TODO(STRFRNT-2867) - remove this after release */}
        {productOption.displayType === 'Text Box' ? `: ${optionNameSelectedValueMap[productOption.name] || ''}` : ''}
        {productOption.displayType === 'Input' || productOption.displayType === 'Text Box' ? '' : ': '}
        {poValueDisplayNameMap?.[optionNameSelectedValueMap[productOption.name]] || (
          <span className="sr-only">not selected</span>
        )}
        {!hideSizeChart &&
          productOption.name === ProductOptionName.Size &&
          (sizeChart || mergedSizeChart) &&
          !productOption.isHidden &&
          renderSizeChartButton(handleSizeChart, showInlineSizeChart)}
      </legend>
      <OptionSelector
        className={classnames(styles.container__options__option, optionSelectorClassName)}
        optionValueList={optionValues}
        selectedValue={optionNameSelectedValueMap[productOption.name]}
        onSelect={value => {
          onOptionSelect(productOption.name, value)
        }}
        isAllRoundOptions={isAllRoundOptions}
        product={product}
        selectorKey={QUINCE_BOT_LOCATORS.PDP.FIRST_PRODUCT_OPTIONS}
        selectedOptions={selectedOptions}
        isPDP={true}
        optionDisplayTypesVsClassName={optionDisplayTypesVsClassName}
        renderAfterColorComponent={renderSwatchSelectionButton}
        displayName={productOption.displayName}
      />
      {!showInlineSizeChart &&
        !hideSizeChart &&
        productOption.name === ProductOptionName.Size &&
        (sizeChart || mergedSizeChart) &&
        !productOption.isHidden && (
          <>
            {showTruefitWidget ? (
              <TrueFitWidget
                productId={product?.id}
                colorId={getSelectedColorFromSelectedOptions(selectedOptions)}
                useDataRec
                parentContainerId="product_purchase"
              />
            ) : null}
          </>
        )}
    </fieldset>
  )
}

export const renderErrorMessage = (errorMessage?: string, className?: string) => {
  return <div className={classnames(styles.container__error_message, className)}>{errorMessage}</div>
}

export default ProductForm
